import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useConsultationContext } from '../../context/ConsultationContext';

import { AppointmentDetailsTab } from '../UI/SelectedSalesOrder/AppointmentDetailsTab';
import { DoctorDetailsTab } from '../UI/SelectedSalesOrder/DoctorDetailsTab';
import { CustomerProfileTab } from '../UI/SelectedSalesOrder/CustomerProfileTab';
import { Documents } from '../../screens/VideoConsultingRoom/VideoMenu/Documents';
import { Findings } from '../../screens/VideoConsultingRoom/VideoMenu/Findings';
import { Recommendations } from '../../screens/VideoConsultingRoom/VideoMenu/Recommendations';
import { InternalNotes } from '../../screens/VideoConsultingRoom/VideoMenu/InternalNotes';
import { VitalSignsTab } from '../../screens/PrepareConsultation/UI/VitalSignsTab';
import { UltraSoundTab } from '../../screens/VideoConsultingRoom/VideoMenu/UltraSoundTab';
import { ExaminationsTab } from '../UI/SelectedSalesOrder/Examinations/ExaminationsTab';
import DigitalHealthProfileTab from '../UI/SelectedSalesOrder/DigitalHealthProfileTab';
import AdditionalServicesTab from '../../screens/VideoConsultingRoom/VideoMenu/AdditionalServicesTab';
import SaveTextArea from './SaveTextArea/SaveTextArea';
import Anamnesis from '../../screens/Anamnesis/Anamnesis';
import Preview from './Preview/Preview';
import styles from './ConsultationParentComponent.module.scss';
import { useTranslation } from '../../hooks/useTranslations';

export default function WorkingArea() {
  const {
    consultationState: { tabsBar },
    deleteTab,
    activateTab,
    addTab,
    selectedSalesOrder,
    doctorMenu,
    finding,
    recommendation,
    internalNotes,
    workOrderStatusId,
    isReports,
  } = useConsultationContext();
  const { translations } = useTranslation();
  const activeTab = tabsBar.find((tab: any) => tab.isVisible);
  // const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);

  const [openDropDown, setOpenDropDown] = useState<boolean>(false);
  const ref = useRef<HTMLInputElement | undefined>();

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      // If the menu is open and the clicked target is not within the menu, then close the menu
      if (openDropDown && ref.current && !ref.current.contains(e.target)) {
        setOpenDropDown(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [openDropDown]);

  const componentKeyVal = useMemo(() => {
    const components: any = {
      0: <CustomerProfileTab selectedSalesOrder={selectedSalesOrder} />,
      1: <AppointmentDetailsTab selectedSalesOrder={selectedSalesOrder} isInConsultations={true} />,
      2: <DoctorDetailsTab selectedSalesOrder={selectedSalesOrder} isReports={isReports} />,
      3: (
        <DigitalHealthProfileTab
          selectedSalesOrder={selectedSalesOrder}
          hasDocuments
          anamnesisResultViewer
        />
      ),
      5: (
        <Findings
          findingId={finding?.consultation_findings_id ?? 0}
          findingText={finding?.consultation_findings_description ?? ''}
          isClosed={selectedSalesOrder?.work_order?.work_order_status_id == 2}
        />
      ),
      6: (
        <Recommendations
          recommendationId={recommendation?.consultation_recommendations_id ?? 0}
          recommendationText={recommendation?.consultation_recommendations_description ?? ''}
          isClosed={selectedSalesOrder?.work_order?.work_order_status_id == 2}
        />
      ),
      7: <Documents selectedSalesOrder={selectedSalesOrder} />,
      8: (
        <InternalNotes
          internalNoteId={internalNotes?.consultation_notes_id ?? 0}
          internalNoteText={internalNotes?.consultation_notes_description ?? ''}
          isClosed={selectedSalesOrder?.work_order?.work_order_status_id == 2}
        />
      ),
      9: <VitalSignsTab selectedSalesOrder={selectedSalesOrder} />,
      10: <UltraSoundTab />,
      11: <ExaminationsTab selectedSalesOrder={selectedSalesOrder} report={isReports} />,
      12: (
        <AdditionalServicesTab
          workOrderId={selectedSalesOrder?.work_order?.work_order_id}
          isWorkOrderFinalized={
            selectedSalesOrder?.work_order?.work_order_status_id == 2 ||
            selectedSalesOrder?.work_order?.work_order_status_id == 3
          }
        />
      ),
      14: <SaveTextArea type="prescription" />,
      15: <SaveTextArea type="sickLeave" />,
      16: (
        <Anamnesis
          anamnesisType="consultation"
          personId={selectedSalesOrder.customer_person?.person_id}
          personExternalId={selectedSalesOrder.customer_person?.person_external_id}
        />
      ),
      17: <Preview />,
    };
    return components;
  }, [selectedSalesOrder, finding, recommendation, internalNotes]);

  const dotsMenuItems: any[] = useMemo(() => {
    return doctorMenu.filter((el: any) => ![5, 6, 7, 8, 17].includes(el.value)) ?? [];
  }, [doctorMenu]);

  return (
    <>
      <div className={styles.tabs}>
        <ul>
          {tabsBar?.map((tab: any, index: number) => (
            <li
              className={tab.isVisible ? styles.active : ''}
              key={index}
              onClick={() => activateTab(tab)}
            >
              {translations(tab.text)}
              <button onClick={(e) => deleteTab(e, tab)} className={styles.beleteBtn}>
                <i className={styles.xIcon} />
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.workingArea}>
        <div style={{ height: '100%', padding: '10px' }}>
          {componentKeyVal[activeTab?.value as keyof typeof componentKeyVal]}
        </div>
      </div>
      {workOrderStatusId && (
        <>
          {/* {dotsMenuItems.length > 0 && (
            <div className={styles.doctorMenu}>
              <div className={styles.mainItems}>
                {dotsMenuItems.map((item, idx) => {
                  return (
                    <div onClick={() => addTab(item)} key={idx} className={styles.itemMenu}>
                      {translations(item.text)}
                    </div>
                  );
                })}
              </div>
            </div>
          )} */}
          <div className={styles.doctorMenu}>
            <div className={styles.mainItems}>
              {doctorMenu
                .filter((el: any) => [5, 6, 7, 8].includes(el.value))
                .map((item: any, idx: number) => {
                  return (
                    <div onClick={() => addTab(item)} key={idx} className={styles.itemMenu}>
                      {translations(item.text)}
                    </div>
                  );
                })}
              {dotsMenuItems.map((item, idx) => {
                return (
                  <div onClick={() => addTab(item)} key={idx} className={styles.itemMenu}>
                    {translations(item.text)}
                  </div>
                );
              })}
            </div>

            {/* Dots dropDown its hiden */}

            {/* {dotsMenuItems.length > 0 && (
              <div className={styles.dropdown} ref={ref as React.RefObject<HTMLDivElement>}>
                <i
                  className={styles.threeDotsIcon}
                  onClick={() => {
                    setOpenDropDown(!openDropDown);
                  }}
                ></i>
                <ul
                  className={openDropDown ? `${styles.list} ${styles._visible}` : `${styles.list}`}
                >
                  {dotsMenuItems.map((item: any, index: number) => (
                    <li
                      key={index}
                      className={`${styles.item}`}
                      onClick={() => {
                        addTab(item);
                        setOpenDropDown(false);
                      }}
                    >
                      {`${translations(item.text)}`}
                    </li>
                  ))}
                </ul>
              </div>
            )} */}
          </div>
        </>
      )}
    </>
  );
}
